import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Layout } from '@/shell/Layout'
import type { ModuleListItem, OrgItem, ServeOverrideAction } from '@/lib/apiTypes'
import { useOrgs, useModules, useAccessToken } from '@/lib/hooks'
import { ServeOverrideControl, ServeOverrideBadge } from '@/shared/components/ServeOverrideControl'
import { api } from '@/lib/api'
import { PublishModal } from '@/features/modules/components/PublishModal'
import { DeprecateModuleModal, UndeprecateModuleModal } from '@/shared/components/ModuleModals'
import { ConfirmModal } from '@/shared/components/ConfirmModal'
import { useToast } from '@/context/ToastContext'
import { PageLoading, EmptyState } from '@/shared/components/EmptyState'
import { EmptyGuide, useGuideNavigate } from '@/features/integrations/components/EmptyGuide'
import { ModulePublishingGuide } from '@/features/integrations/guides/ModulePublishingGuide'
import { GUIDED_ONBOARDING_TIERS } from '@/lib/tiers'
import { SkeletonTable } from '@/shared/components/Skeleton'
import { SecurityBadge } from '@/features/modules/components/SecurityBadge'
import { BlockedBadge } from '@/shared/components/BlockedBadge'
import { SourceRepoLink } from '@/shared/components/SourceRepoLink'
import { formatCount } from '@/lib/format'
import { IconButton, MetricStat } from '@/shared/components/IconButton'
import { PageIntro } from '@/shared/components/PageIntro'
import { PlusIcon, RescanIcon, ArchiveIcon, RestoreIcon, TrashIcon, TagIcon, DownloadIcon } from '@/shared/components/Icons'
import { Tooltip } from '@/shared/components/Tooltip'
// ── Root page ──────────────────────────────────────────────────────────────────

export function Modules() {
  const { orgSlug } = useParams<{ orgSlug: string }>()
  const orgs = useOrgs()
  const org = orgs.data?.orgs.find((o) => (o.slug ?? o.id) === orgSlug)

  if (orgs.loading) return (
    <Layout title="// Modules">
      <PageLoading />
    </Layout>
  )

  return (
    <Layout title="Modules">
      {org ? (
        <OrgModulesPanel org={org} />
      ) : null}
    </Layout>
  )
}

// ── Org modules panel (right side) ─────────────────────────────────────────────

function OrgModulesPanel({ org }: { org: OrgItem }) {
  const canMember = ['owner', 'admin', 'member'].includes(org.role)
  const canAdmin = ['owner', 'admin'].includes(org.role)

  return (
    <div>
      <PageIntro pageKey="modules" title="What is this page?">
        <p style={{ margin: '0 0 10px' }}>
          Publish versioned Terraform modules here from the CLI or CI and they appear with full
          version history, download counts, and security scan results. Each publish is scanned for
          secrets, CVEs and malware before it's ever served - unscanned source never reaches a
          <code className="mono"> tofu init</code>.
        </p>
        <p style={{ margin: 0 }}>
          Open a module for its per-version findings, used-by/referenced-by data, and release
          management: deprecate a module to warn consumers without pulling it (versions stay
          downloadable), queue a rescan, or delete it outright.
        </p>
      </PageIntro>

      {canMember && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginBottom: 20 }}>
          <PublishButton slug={org.slug} />
        </div>
      )}

      <ModulesTab org={org} slug={org.slug} canMember={canMember} canAdmin={canAdmin} />
    </div>
  )
}

// ── Publish button ─────────────────────────────────────────────────────────────

function PublishButton({ slug }: { slug: string }) {
  const [show, setShow] = useState(false)
  return (
    <>
      {show && <PublishModal slug={slug} onClose={() => setShow(false)} onSuccess={() => setShow(false)} />}
      <button className="btn btn-primary btn-sm" onClick={() => setShow(true)}>
        <PlusIcon size={13} /> Publish
      </button>
    </>
  )
}

// ── Modules tab ────────────────────────────────────────────────────────────────

const RESCAN_ALL_COOLDOWN_MS = 60 * 60 * 1000

function ModulesTab({ org, slug, canMember, canAdmin }: { org: OrgItem; slug: string; canMember: boolean; canAdmin: boolean }) {
  const navigate = useNavigate()
  const goToGuide = useGuideNavigate()
  const { token } = useAccessToken()
  const { showToast } = useToast()
  const [search] = useState('')
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [deprecatingModule, setDeprecatingModule] = useState<ModuleListItem | null>(null)
  const [undeprecatingModule, setUndeprecatingModule] = useState<ModuleListItem | null>(null)
  const [rescanAllBusy, setRescanAllBusy] = useState<string | null>(null)
  const [overrideBusy, setOverrideBusy] = useState<string | null>(null)
  const [secSort, setSecSort] = useState<'desc' | 'asc' | null>(null)
  const [nameSort, setNameSort] = useState<'asc' | 'desc' | null>(null)
  const [dlSort, setDlSort] = useState<'desc' | 'asc' | null>(null)
  const [verSort, setVerSort] = useState<'desc' | 'asc' | null>(null)
  const [statusSort, setStatusSort] = useState<'asc' | 'desc' | null>(null)
  const clearOthers = (...setters: Array<(v: null) => void>) => setters.forEach(s => s(null))
  const modules = useModules(slug, search)
  const secScore = (m: ModuleListItem) => (m.latest_critical ?? 0) * 10000 + (m.latest_high ?? 0) * 100 + (m.latest_low ?? 0)
  const sortedModules = (() => {
    const base = modules.data?.modules ?? []
    if (secSort) return [...base].sort((a, b) => secSort === 'desc' ? secScore(b) - secScore(a) : secScore(a) - secScore(b))
    if (nameSort) return [...base].sort((a, b) => nameSort === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name))
    if (dlSort) return [...base].sort((a, b) => dlSort === 'desc' ? b.download_count - a.download_count : a.download_count - b.download_count)
    if (verSort) return [...base].sort((a, b) => verSort === 'desc' ? (b.version_count ?? 0) - (a.version_count ?? 0) : (a.version_count ?? 0) - (b.version_count ?? 0))
    if (statusSort) return [...base].sort((a, b) => {
      const rank = (m: ModuleListItem) => m.deprecated ? 1 : 0
      return statusSort === 'asc' ? rank(a) - rank(b) : rank(b) - rank(a)
    })
    return base
  })()

  async function handleRescanAll(m: ModuleListItem) {
    setRescanAllBusy(m.id)
    try {
      const res = await api.post<{ ok: boolean; queued: number }>(
        `/v1/modules/${slug}/${m.name}/${m.provider}/rescan-all`,
        {},
      )
      showToast(`Rescan queued for ${res.queued} version(s) of ${m.name}.`, 'success')
      modules.refetch()
    } catch (err) {
      showToast((err as Error).message, 'error')
    } finally {
      setRescanAllBusy(null)
    }
  }

  async function handleServeOverride(m: ModuleListItem, action: ServeOverrideAction, reason?: string) {
    setOverrideBusy(m.id)
    try {
      await api.put(
        `/v1/modules/${slug}/${m.name}/${m.provider}/serve-override`,
        { action, ...(reason ? { reason } : {}) },
      )
      showToast(action === 'block'
        ? `${m.name} force-blocked - no version will be served.`
        : action === 'allow'
          ? `${m.name} force-allowed - serves even if policy-blocked.`
          : `${m.name} back to Auto - policies decide again.`)
      modules.refetch()
    } catch (err) {
      showToast((err as Error).message, 'error')
    } finally {
      setOverrideBusy(null)
    }
  }

  async function handleDelete(name: string) {
    setBusy(true)
    try {
      await api.delete(`/api/namespaces/${slug}/modules/${name}`)
      setConfirmDelete(null)
      showToast(`${name} deleted.`, 'success')
      modules.refetch()
    } catch (err) {
      showToast((err as Error).message, 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      {deprecatingModule && (
        <DeprecateModuleModal
          namespace={slug}
          name={deprecatingModule.name}
          provider={deprecatingModule.provider}
          onClose={() => setDeprecatingModule(null)}
          onDeprecated={() => {
            setDeprecatingModule(null)
            showToast(`${deprecatingModule.name} deprecated.`, 'success')
            modules.refetch()
          }}
        />
      )}
      {undeprecatingModule && (
        <UndeprecateModuleModal
          namespace={slug}
          name={undeprecatingModule.name}
          provider={undeprecatingModule.provider}
          onClose={() => setUndeprecatingModule(null)}
          onUndeprecated={() => {
            setUndeprecatingModule(null)
            showToast(`${undeprecatingModule.name} is now active.`, 'success')
            modules.refetch()
          }}
        />
      )}
      {modules.loading && (
        <SkeletonTable
          headers={['Module', 'Latest', 'Source', 'Versions', 'Security', 'Downloads', 'Status', ...(canMember ? [''] : [])]}
        />
      )}
      {!modules.loading && modules.error && <div className="empty-state" style={{ color: 'var(--red)' }}>{modules.error}</div>}

      {(modules.data?.modules.length ?? 0) > 0 && (
        <div className="table-wrap fu">
          <table>
            <thead>
              <tr>
                <th>
                  <Tooltip text="Module name and description. Click to sort by name." placement="top">
                    <button
                      onClick={() => { clearOthers(setSecSort, setDlSort, setVerSort, setStatusSort); setNameSort((s) => s === null ? 'asc' : s === 'asc' ? 'desc' : null) }}
                      className="sort-btn"
                    >
                      Module <span className={`sort-indicator${nameSort ? ' sort-indicator--active' : ''}`}>{nameSort === 'asc' ? '↑' : nameSort === 'desc' ? '↓' : '⇅'}</span>
                    </button>
                  </Tooltip>
                </th><th><Tooltip text="Newest published version of this module." placement="top"><span tabIndex={0} className="cursor-help">Latest</span></Tooltip></th>
                <th>
                  <Tooltip text="Repository, ref and commit the latest version was published from." placement="top">
                    <span tabIndex={0} className="cursor-help">Source</span>
                  </Tooltip>
                </th>
                <th>
                  <Tooltip text="How many versions of this module are published. Click to sort." placement="top">
                    <button
                      onClick={() => { clearOthers(setNameSort, setSecSort, setDlSort, setStatusSort); setVerSort((s) => s === null ? 'desc' : s === 'desc' ? 'asc' : null) }}
                      className="sort-btn"
                    >
                      Versions <span className={`sort-indicator${verSort ? ' sort-indicator--active' : ''}`}>{verSort === 'desc' ? '↓' : verSort === 'asc' ? '↑' : '⇅'}</span>
                    </button>
                  </Tooltip>
                </th>
                <th>
                  <Tooltip text="Worst-severity findings from the latest version’s scan. Click to sort." placement="top">
                    <button
                      onClick={() => { clearOthers(setNameSort, setDlSort, setVerSort, setStatusSort); setSecSort((s) => s === null ? 'desc' : s === 'desc' ? 'asc' : null) }}
                      className="sort-btn"
                    >
                      Security <span className={`sort-indicator${secSort ? ' sort-indicator--active' : ''}`}>{secSort === 'desc' ? '↓' : secSort === 'asc' ? '↑' : '⇅'}</span>
                    </button>
                  </Tooltip>
                </th>
                <th>
                  <Tooltip text="Downloads of this module through the registry. Click to sort." placement="top">
                    <button
                      onClick={() => { clearOthers(setNameSort, setSecSort, setVerSort, setStatusSort); setDlSort((s) => s === null ? 'desc' : s === 'desc' ? 'asc' : null) }}
                      className="sort-btn"
                    >
                      Downloads <span className={`sort-indicator${dlSort ? ' sort-indicator--active' : ''}`}>{dlSort === 'desc' ? '↓' : dlSort === 'asc' ? '↑' : '⇅'}</span>
                    </button>
                  </Tooltip>
                </th>
                <th>
                  <Tooltip text="Active, deprecated or blocked - plus any serve override. Click to sort." placement="top">
                    <button
                      onClick={() => { clearOthers(setNameSort, setSecSort, setDlSort, setVerSort); setStatusSort((s) => s === null ? 'asc' : s === 'asc' ? 'desc' : null) }}
                      className="sort-btn"
                    >
                      Status <span className={`sort-indicator${statusSort ? ' sort-indicator--active' : ''}`}>{statusSort === 'asc' ? '↑' : statusSort === 'desc' ? '↓' : '⇅'}</span>
                    </button>
                  </Tooltip>
                </th>
                {canMember && <th></th>}
              </tr>
            </thead>
            <tbody>
              {sortedModules.map((m) => (
                <React.Fragment key={m.id}>
                  <tr
                    className="row-link"
                    data-testid="module-card"
                    onClick={() => navigate(`/org/${slug}/modules/${m.name}/${m.provider}`)}
                  >
                    <td>
                      <div style={{ fontFamily: 'var(--mono)', fontSize: '0.82rem' }}>{m.name}</div>
                      {m.description && <div style={{ fontSize: '0.72rem', color: 'var(--text2)' }}>{m.description}</div>}
                    </td>
                    <td>
                      {m.latest_version
                        ? <span style={{ fontFamily: 'var(--mono)', fontSize: '0.78rem', color: 'var(--green)' }}>v{m.latest_version}</span>
                        : <span className="dim">-</span>}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {m.latest_vcs_repository
                        ? <SourceRepoLink
                            provider={m.latest_vcs_provider}
                            repository={m.latest_vcs_repository}
                            gitRef={m.latest_vcs_ref}
                            refType={m.latest_vcs_ref_type}
                            sha={m.latest_vcs_sha}
                            commitUrl={m.latest_vcs_commit_url}
                            verb="published"
                          />
                        : <span className="dim">-</span>}
                    </td>
                    <td>
                      <MetricStat icon={<TagIcon />} value={m.version_count != null ? formatCount(m.version_count) : '-'} tip="Published versions of this module" />
                    </td>
                    <td><SecurityBadge status={m.latest_scan_status} critical={m.latest_critical ?? 0} high={m.latest_high ?? 0} low={m.latest_low ?? 0} /></td>
                    <td>
                      <MetricStat icon={<DownloadIcon />} value={formatCount(m.download_count)} tip="Downloads through the registry" />
                    </td>
                    <td>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                        {m.serve_override && <ServeOverrideBadge value={m.serve_override} reason={m.serve_override_reason} />}
                        {m.blocked
                          ? <BlockedBadge reason={m.block_reason} />
                          : m.deprecated
                            ? <span className="badge badge-amber">deprecated</span>
                            : !m.serve_override && <span className="badge badge-green">active</span>}
                      </span>
                    </td>
                    {canMember && (
                      <td style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {(() => {
                          const cooldownUntil = m.bulk_rescan_requested_at ? m.bulk_rescan_requested_at + RESCAN_ALL_COOLDOWN_MS : null
                          const onCooldown = cooldownUntil !== null && Date.now() < cooldownUntil
                          return (
                            <IconButton
                              label="Rescan"
                              tip="Queue a security rescan of all versions"
                              disabledReason={onCooldown ? `Available after ${new Date(cooldownUntil!).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : null}
                              busy={rescanAllBusy === m.id}
                              onClick={() => handleRescanAll(m)}
                            >
                              <RescanIcon />
                            </IconButton>
                          )
                        })()}
                        {canAdmin && !m.deprecated && (
                          <IconButton
                            label="Deprecate"
                            tip="Mark deprecated - warns consumers, versions stay downloadable"
                            tint="var(--amber)"
                            onClick={() => setDeprecatingModule(m)}
                          >
                            <ArchiveIcon />
                          </IconButton>
                        )}
                        {canAdmin && m.deprecated && (
                          <IconButton
                            label="Undeprecate"
                            tip="Restore this module to active status"
                            tint="var(--green)"
                            onClick={() => setUndeprecatingModule(m)}
                          >
                            <RestoreIcon />
                          </IconButton>
                        )}
                        <IconButton
                          label="Delete"
                          tip="Delete this module and all its versions"
                          tint="var(--red)"
                          onClick={() => setConfirmDelete(m.name)}
                        >
                          <TrashIcon />
                        </IconButton>
                        {canAdmin && (
                          <ServeOverrideControl
                            value={m.serve_override}
                            subject={m.name}
                            busy={overrideBusy === m.id}
                            compact
                            onApply={(action, reason) => handleServeOverride(m, action, reason)}
                          />
                        )}
                      </td>
                    )}
                  </tr>
                  {confirmDelete === m.name && (
                    <ConfirmModal
                      track="module_delete_confirm"
                      title={`Delete ${m.name}`}
                      message={`Delete ${m.name}? This permanently removes all versions and scan data.`}
                      confirmLabel="Delete"
                      danger
                      busy={busy}
                      onConfirm={() => handleDelete(m.name)}
                      onCancel={() => setConfirmDelete(null)}
                    />
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!modules.loading && modules.data?.modules.length === 0 && (
        !search && GUIDED_ONBOARDING_TIERS.has(org.tier) ? (
          <EmptyGuide
            icon="⬡"
            title="No modules yet"
            blurb="Publish your first module from the CLI or CI and it appears here, scanned and versioned. The guide below already has this org's slug and a token filled in."
            guideId="module-publishing"
          >
            <ModulePublishingGuide org={org} token={token} onSwitchGuide={goToGuide} />
          </EmptyGuide>
        ) : (
          <EmptyState icon="⬡" title={search ? `No modules matching "${search}"` : 'No modules yet.'} />
        )
      )}
    </div>
  )
}


